import { Badge, Icon, IconPack, Typography } from '@healingpaper/cell'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { t } from 'i18next'

import PreviewPhotoSlider, { type PreviewPhoto } from '@/components/atomic/organisms/PreviewPhotoSlider'
import UserProfile from '@/components/atomic/organisms/UserProfile'
import { postAmplitude } from '@/core/lib'
import { Review } from '@/features/review/api/review.type'

export interface Props {
  text: {
    before: string
    after: string
    receiptCheck: string
    translate: string
    modified: string
  }
  containerRef?: React.Ref<HTMLDivElement>
  containerId?: string
  review: Review
  onClickReviewCard?: (review: Review) => void
}

const ReviewCard = ({ text, containerRef: ref, containerId, review, onClickReviewCard }: Props): JSX.Element => {
  const [useEllipsis, setUseEllipsis] = useState(true)
  const photos: PreviewPhoto[] = useMemo(() => {
    return [
      ...(review.beforePhotos?.map((photo) => ({
        title: text.before,
        url: photo.url,
        blurred: photo.blurred,
        alt: `before photo: ${review.treatments.map((treatment) => treatment.name).join(', ')}`,
      })) || []),
      ...(review.afterPhotos?.map((photo) => ({
        title: text.after,
        url: photo.url,
        blurred: photo.blurred,
        alt: `after photo: ${review.treatments.map((treatment) => treatment.name).join(', ')}`,
      })) || []),
    ]
  }, [review, text])

  const handleClickProfile = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClickReviewCard?.(review)
  }

  const handleClickDescription = (e: React.MouseEvent) => {
    e.stopPropagation()
    setUseEllipsis(false)
    onClickReviewCard?.(review)
    postAmplitude('click_review_seemore')
  }

  const handleClickItem = (target: 'review') => {
    switch (target) {
      case 'review':
        onClickReviewCard?.(review)
        return
    }
  }

  const description = review.translation?.description || review.description

  return (
    <StyledContainer id={containerId} ref={ref}>
      <StyledReviewCard onClick={() => handleClickItem('review')}>
        <StyledUserProfileWrapper>
          <UserProfile
            nickname={review.author.nickName || ''}
            profileUrl={review.author.profileImage || ''}
            level={`${review.author.level}`}
            description={
              <StyledUserProfileDescription>
                {review.procedureProofApproved && (
                  <Badge size="smallSquared" color="yellow">
                    {text.receiptCheck}
                  </Badge>
                )}
                <StyledDate>
                  {format(new Date(review.postedAt), 'yyyy.MM.dd')}
                  {review.postedAt !== review.editedLastAt && ` (${text.modified})`}
                </StyledDate>
              </StyledUserProfileDescription>
            }
            onClick={handleClickProfile}
          />

          {!!review.totalRating && review.totalRating > 0 && (
            <StyledRatingWrapper aria-label="review rating">
              <StyledRatingIcon name={IconPack.GENERAL_FEATURE_EVALUATION_FULL} width={14} height={14} />
              <StyledRating>{Math.round(review.totalRating)}</StyledRating>
            </StyledRatingWrapper>
          )}
        </StyledUserProfileWrapper>

        {review.treatments.length > 0 && (
          <StyledTreatmentTagList>{review.treatments.map(({ name }) => `#${name}`).join(' ')}</StyledTreatmentTagList>
        )}

        {!!photos.length && (
          <StyledPreviewPhotoSlider
            useViewer
            photos={photos}
            aria-label="review photos"
            onClickPhoto={() => {
              postAmplitude('click_review_photo')
            }}
          />
        )}

        {!!description && (
          <StyledDescription
            $useEllipsis={useEllipsis}
            onClick={(e) => {
              handleClickDescription(e)
            }}
          >
            {description}
          </StyledDescription>
        )}

        {review.descriptionLanguage &&
          review.translation?.language &&
          review.translation?.language !== review.descriptionLanguage && (
            <div>
              <Typography type="caption2" color="gray700">
                {t('webCommunityTranslationDescription', {
                  language: t(getTranslationLanguageKey(review.descriptionLanguage as 'ko' | 'en' | 'ja')),
                })}
              </Typography>
            </div>
          )}
      </StyledReviewCard>
    </StyledContainer>
  )
}

const getTranslationLanguageKey = (language: 'ko' | 'en' | 'ja') => {
  switch (language) {
    case 'en':
      return 'webCommunityTranslationEnglish'
    case 'ja':
      return 'webCommunityTranslationJapanese'
    default:
      return 'webCommunityTranslationKorean'
  }
}

const StyledContainer = styled.div`
  height: auto;
  box-sizing: border-box;
`

const StyledReviewCard = styled.div`
  padding: 16px 0;
  display: grid;
  flex-direction: column;
  gap: 16px;
  background-color: ${({ theme }) => theme.colors.white};
`

const StyledUserProfileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const StyledUserProfileDescription = styled.div`
  display: grid;
  gap: 8px;
  grid-auto-flow: column;
  align-items: center;
`

const StyledDate = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
  ${({ theme }) => theme.typography.caption2}
`

const StyledRatingWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
`

const StyledRatingIcon = styled(Icon)`
  * {
    fill: ${({ theme }) => theme.colors.yellow};
  }
`

const StyledRating = styled.span`
  color: ${({ theme }) => theme.colors.black};
  ${({ theme }) => theme.typography.title4}
  line-height: 14px;
`

const StyledTreatmentTagList = styled.p`
  margin: 0px;
  padding: 0px;
  color: ${({ theme }) => theme.colors.latte};
  ${({ theme }) => theme.typography.description2}
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  word-spacing: 8px;
`

const StyledPreviewPhotoSlider = styled(PreviewPhotoSlider)`
  margin-left: -16px;
  width: calc(100% + 32px);
`

const StyledDescription = styled.p<{ $useEllipsis: boolean }>`
  white-space: pre-line;
  cursor: auto;
  height: auto;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  ${({ $useEllipsis }) =>
    $useEllipsis &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      max-height: 60px;
      white-space: normal;
      cursor: pointer;
    `}

  ${({ theme }) => theme.typography.description2}
`

export default ReviewCard
